import { PersistenceError } from "@kortyx/core/errors";
import { createRedisClient, type RedisClient } from "./redis-client";

export type RedisFrameworkStoreOptions = {
  url: string;
  prefix?: string;
};

export type FrameworkTtl = {
  ttlMs: number;
};

export type RedisFrameworkStore = {
  close?: () => Promise<void>;
  list?: (prefix: string) => Promise<string[]>;
  take?: (key: string) => Promise<string | null>;
  get: (key: string) => Promise<string | null>;
  set: (key: string, value: string, ttlMs: number) => Promise<void>;
  del: (key: string) => Promise<void>;
  hset: (key: string, field: string, value: string) => Promise<void>;
  hsetnx: (key: string, field: string, value: string) => Promise<number>;
  writeHash?: (
    key: string,
    writes: Array<{ field: string; value: string; overwrite: boolean }>,
    ttlMs: number,
  ) => Promise<void>;
  hgetall: (key: string) => Promise<Record<string, string>>;
  expire: (key: string, ttlMs: number) => Promise<void>;
  scanKeys: (prefix: string) => Promise<string[]>;
  delRaw: (keys: string[]) => Promise<void>;
};

type RedisErrorReply = { type: "error"; message: string };
const isRedisError = (r: unknown): r is RedisErrorReply =>
  Boolean(r) && typeof r === "object" && (r as any).type === "error";

export function createRedisFrameworkStore(
  options: RedisFrameworkStoreOptions,
): RedisFrameworkStore {
  const rawClient = createRedisClient({ url: options.url });
  const client: RedisClient = {
    ...rawClient,
    command: async (...args) => {
      try {
        return await rawClient.command(...args);
      } catch (cause) {
        throw new PersistenceError("Redis command failed.", cause);
      }
    },
  };
  const prefix = options.prefix ?? "kortyx:fw:";

  const k = (key: string) => `${prefix}${key}`;

  return {
    close: async () => rawClient.close?.(),
    async list(prefixKey) {
      const keys: string[] = await this.scanKeys(prefixKey);
      const values = await Promise.all(
        [...new Set(keys)].map(async (key) => {
          const value = await client.command("GET", [key]);
          if (isRedisError(value))
            throw new PersistenceError(`Redis GET error: ${value.message}`);
          return value;
        }),
      );
      return values.filter(
        (value): value is string => typeof value === "string",
      );
    },
    async take(key: string): Promise<string | null> {
      const r = await client.command("GETDEL", [k(key)]);
      if (isRedisError(r))
        throw new PersistenceError(`Redis GETDEL error: ${r.message}`);
      return typeof r === "string" ? r : null;
    },
    async get(key: string): Promise<string | null> {
      const r = await client.command("GET", [k(key)]);
      if (r === null) return null;
      if (typeof r === "string") return r;
      if (isRedisError(r)) {
        throw new PersistenceError(`Redis GET error: ${r.message}`);
      }
      return null;
    },

    async set(key: string, value: string, ttlMs: number): Promise<void> {
      const args = [k(key), value, "PX", String(Math.max(1, ttlMs))];
      const r = await client.command("SET", args);
      if (isRedisError(r)) {
        throw new PersistenceError(`Redis SET error: ${r.message}`);
      }
    },

    async del(key: string): Promise<void> {
      const r = await client.command("DEL", [k(key)]);
      if (isRedisError(r)) {
        throw new PersistenceError(`Redis DEL error: ${r.message}`);
      }
    },

    async hset(key: string, field: string, value: string): Promise<void> {
      const r = await client.command("HSET", [k(key), field, value]);
      if (isRedisError(r)) {
        throw new PersistenceError(`Redis HSET error: ${r.message}`);
      }
    },

    async hsetnx(key: string, field: string, value: string): Promise<number> {
      const r = await client.command("HSETNX", [k(key), field, value]);
      if (isRedisError(r)) {
        throw new PersistenceError(`Redis HSETNX error: ${r.message}`);
      }
      return typeof r === "number" ? r : 0;
    },

    async writeHash(key, writes, ttlMs) {
      const duration = Math.max(1, ttlMs);
      if (!Number.isSafeInteger(duration))
        throw new PersistenceError(
          "Redis checkpoint TTL must be a finite integer.",
        );
      if (!rawClient.transaction)
        throw new PersistenceError(
          "Redis client requires transaction support for checkpoint writes.",
        );
      try {
        await rawClient.transaction([
          ...writes.map(({ field, value, overwrite }): [string, string[]] => [
            overwrite ? "HSET" : "HSETNX",
            [k(key), field, value],
          ]),
          ["PEXPIRE", [k(key), String(duration)]],
        ]);
      } catch (cause) {
        if (cause instanceof PersistenceError) throw cause;
        throw new PersistenceError(
          "Redis checkpoint transaction failed.",
          cause,
        );
      }
    },

    async hgetall(key: string): Promise<Record<string, string>> {
      const r = await client.command("HGETALL", [k(key)]);
      if (isRedisError(r)) {
        throw new PersistenceError(`Redis HGETALL error: ${r.message}`);
      }
      if (!Array.isArray(r)) return {};
      const out: Record<string, string> = {};
      for (let i = 0; i < r.length; i += 2) {
        const kk = r[i];
        const v = r[i + 1];
        if (typeof kk === "string" && typeof v === "string") out[kk] = v;
      }
      return out;
    },

    async expire(key: string, ttlMs: number): Promise<void> {
      const r = await client.command("PEXPIRE", [
        k(key),
        String(Math.max(1, ttlMs)),
      ]);
      if (isRedisError(r)) {
        throw new PersistenceError(`Redis PEXPIRE error: ${r.message}`);
      }
    },

    async scanKeys(prefixKey: string): Promise<string[]> {
      const fullPrefix = k(prefixKey);
      let cursor = "0";
      const keys: string[] = [];
      do {
        const r = await client.command("SCAN", [
          cursor,
          "MATCH",
          `${fullPrefix}*`,
          "COUNT",
          "200",
        ]);
        if (isRedisError(r))
          throw new PersistenceError(`Redis SCAN error: ${r.message}`);
        if (!Array.isArray(r) || r.length < 2) break;
        cursor = typeof r[0] === "string" ? r[0] : "0";
        const batch = r[1];
        if (Array.isArray(batch)) {
          for (const key of batch) {
            if (typeof key === "string") keys.push(key);
          }
        }
      } while (cursor !== "0");
      return keys;
    },

    async delRaw(keys: string[]): Promise<void> {
      if (keys.length === 0) return;
      const r = await client.command("DEL", keys);
      if (isRedisError(r)) {
        throw new PersistenceError(`Redis DEL error: ${r.message}`);
      }
    },
  };
}
